/**
 * チェスの TypeScript 表現 (mixed-engine の serde 形と一致)。
 */

export type Side = 'First' | 'Second';

export type PieceKind = 'Pawn' | 'Knight' | 'Bishop' | 'Rook' | 'Queen' | 'King';

export interface Piece {
  kind: PieceKind;
  owner: Side;
}

export interface CastlingRights {
  wk: boolean;
  wq: boolean;
  bk: boolean;
  bq: boolean;
}

export type ChessOutcomeReason =
  | 'Checkmate'
  | 'Resignation'
  | 'Timeout'
  | 'Stalemate'
  | 'FiftyMove'
  | 'SeventyFiveMove'
  | 'Threefold'
  | 'Fivefold'
  | 'InsufficientMaterial'
  | 'DrawAgreement';

export interface ChessOutcome {
  winner: Side | null;
  reason: ChessOutcomeReason;
}

export interface ChessState {
  board: (Piece | null)[][];
  side: Side;
  castling: CastlingRights;
  ep_target: [number, number] | null;
  halfmove_clock: number;
  fullmove_number: number;
  position_history: string[];
  outcome: ChessOutcome | null;
}

export type ChessMove =
  | { Normal: { from: [number, number]; to: [number, number] } }
  | {
      Promotion: {
        from: [number, number];
        to: [number, number];
        promote_to: PieceKind;
      };
    }
  | { Castle: { side: Side; kingside: boolean } }
  | {
      EnPassant: {
        from: [number, number];
        to: [number, number];
        captured: [number, number];
      };
    };

export const CHESS_SIZE = 8;

export function isNormalMove(m: ChessMove): m is { Normal: { from: [number, number]; to: [number, number] } } {
  return typeof m === 'object' && 'Normal' in m;
}
export function isPromotion(m: ChessMove): m is {
  Promotion: { from: [number, number]; to: [number, number]; promote_to: PieceKind };
} {
  return typeof m === 'object' && 'Promotion' in m;
}
export function isCastle(m: ChessMove): m is { Castle: { side: Side; kingside: boolean } } {
  return typeof m === 'object' && 'Castle' in m;
}
export function isEnPassant(m: ChessMove): m is {
  EnPassant: { from: [number, number]; to: [number, number]; captured: [number, number] };
} {
  return typeof m === 'object' && 'EnPassant' in m;
}

/** legal の中から from が一致する全 to 候補を抽出 (UI で from クリック → to 候補ハイライト用)。 */
export function legalTargetsFor(legal: ChessMove[], from: [number, number]): ChessMove[] {
  return legal.filter((m) => {
    if (isNormalMove(m)) return m.Normal.from[0] === from[0] && m.Normal.from[1] === from[1];
    if (isPromotion(m)) return m.Promotion.from[0] === from[0] && m.Promotion.from[1] === from[1];
    if (isEnPassant(m)) return m.EnPassant.from[0] === from[0] && m.EnPassant.from[1] === from[1];
    return false;
  });
}

export function moveTo(m: ChessMove): [number, number] | null {
  if (isNormalMove(m)) return m.Normal.to;
  if (isPromotion(m)) return m.Promotion.to;
  if (isEnPassant(m)) return m.EnPassant.to;
  return null;
}
